import { Button } from "@/components/UI/Button";
import { getMainBlueActionButtonSize } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { generateToastMessageForTradeFlowErrors } from "@/lib/notifications/toastNotificationHelpers";
import expandedRowsStore from "@/store/expandedRowsStore";
import useTableUIStore from "@/store/tableUIStore";
import tradeDataStore from "@/store/tradeDataStore";
import tradesInProcessStore from "@/store/tradesInProcessStore";
import { useAuth0 } from "@auth0/auth0-react";
import { ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { getActionsMainButton } from "./MainActionButtonActionSummaryHelper";

export function addDisabledButtonToStoreArray(esid: string) {
  const newArr = [...tradesInProcessStore.getState().tradesInProcess];
  if (!newArr.includes(esid)) {
    newArr.push(esid);
    tradesInProcessStore.getState().addTradeInProcess(newArr);
  }
}

export interface IMainAcitonButtonActionSummary extends ICellRendererParams {
  cryptoID?: string | undefined;
  gridRef: React.RefObject<AgGridReact<any>>;
}

const MainActionButtonActionSummary = (
  params: IMainAcitonButtonActionSummary,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const { data, cryptoID, gridRef } = params;

  const fontSize = useTableUIStore((state) => state.fontSize);
  const dynamicButtonSize = useMemo(
    () => getMainBlueActionButtonSize(fontSize),
    [fontSize],
  );

  // Subscribe to Zustand store for this widget's expanded rows
  const expandedRowsContext = expandedRowsStore((state) =>
    cryptoID ? state.getExpandedRows(cryptoID) : [],
  );

  const tradesInProcess = tradesInProcessStore(
    (state) => state.tradesInProcess,
  );

  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade = currentTradeData.find((trade) => trade.esid == data.esid);

  const [buttonDisabled, setButtonDisabled] = useState(
    tradesInProcess.includes(params.data.esid) || false,
  );

  // First use effect cannot also set button active again as it will set before the new status has updated the button actions.
  useEffect(() => {
    if (
      thisTrade?.status == ExecutionStrategyStatus.Affirmed // Specific to RP - Showing legs action only.
    ) {
      setButtonDisabled(false);
    } else if (tradesInProcess.includes(params.data.esid)) {
      setButtonDisabled(true);
    } else {
      setButtonDisabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradesInProcess]);

  // Second use effect becomes aware of new trade data and then checks to see if the button can be made active again.
  useEffect(() => {
    if (
      // thisTrade?.status == ExecutionStrategyStatus.New ||
      // thisTrade?.status == ExecutionStrategyStatus.Quoted ||
      thisTrade?.status == ExecutionStrategyStatus.Affirmed
    ) {
      setButtonDisabled(false);
    } else setButtonDisabled(tradesInProcess.includes(params.data.esid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, thisTrade]);

  const [isExpandedNode, setIsExpandedNode] = useState(false);

  const verifyTimeInput = () => {
    let isValid = true;
    if (params.node.data.role == "RP" && params.node.data.expiryPeriod == 0) {
      isValid = false;
    } else if (params.node.data.role == "OP") {
      if (params.node.data.executionRequestCounterParties) {
        params.node.data.executionRequestCounterParties.forEach((ERCP: any) => {
          if (ERCP.expiryPeriod == 0) {
            isValid = false;
          }
        });
      }
    }
    return isValid;
  };

  const [mainBlueAction, setMainBlueAction] = useState(
    params.data && params.data.type
      ? getActionsMainButton(
          params.data.type.toString().replace(/\s+/g, ""),
          thisTrade?.status || params.data.status,
          params,
        )[0]
      : null,
  );

  useEffect(() => {
    const newAction =
      params.data && params.data.type
        ? getActionsMainButton(
            params.data.type.toString().replace(/\s+/g, ""),
            thisTrade?.status || params.data.status,
            params,
          )[0]
        : null;
    setMainBlueAction(newAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.data.type, params.data.status, thisTrade?.status]);

  useEffect(() => {
    const handleRowGroupOpened = (event: any) => {
      if (event.node.data) {
        if (event.node.data.esid === params.data.esid) {
          if (gridRef && gridRef.current) {
            const updatedNode = gridRef.current.api.getRowNode(
              params.data.esid,
            );
            if (updatedNode && updatedNode.expanded) {
              setIsExpandedNode(true);
            } else {
              setIsExpandedNode(false);
            }
          }
        }
      }
    };

    if (gridRef && gridRef.current) {
      const api = gridRef.current?.api;
      if (api) {
        if (!api.isDestroyed()) {
          api.addEventListener("rowGroupOpened", handleRowGroupOpened);
        }
      }

      return () => {
        if (api) {
          if (!api.isDestroyed()) {
            api.removeEventListener("rowGroupOpened", handleRowGroupOpened);
          }
        }
      };
    }
  }, [gridRef, params.data.esid]);

  async function newButtonAction() {
    // setButtonDisabled(false);
    //Update the trade store
    const currentStore = tradeDataStore.getState().tradeData;
    const newData = currentStore.map((trade) => {
      return trade.esid == params.node.data.esid
        ? {
            ...trade,
            attemptSendTrade: trade.attemptSendTrade ? false : true,
            cryptoUpdated: cryptoID,
          }
        : trade;
    });
    tradeDataStore.getState().updateTradeData(newData);
    // Main function will set the button to no longer disabled
    mainBlueAction.function({
      data: thisTrade || params.node.data,
      getAccessTokenSilently: getAccessTokenSilently,
      // setButtonDisabled: setButtonDisabled,
    });
  }

  // Check on load if the node is already expanded. This will be needed when 'Selecting Panel' from the creation rather than saving to the table. Also scrolling out of sight from the trade and scolling back to it still in an expanded state.
  useEffect(() => {
    const api = gridRef?.current?.api;
    const updatedNode = api?.getRowNode(params.data.esid);
    const isExpanded = updatedNode?.expanded;
    if (isExpanded) {
      setIsExpandedNode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function determineButtonDisplayName() {
    const currentTrade = expandedRowsContext.find(
      (obj) => obj.esid == params.data.esid,
    );
    if (mainBlueAction.id == "Send to Panel") {
      //#14724 update Close leg and close allocations to only show/replace 'Send to Panel' action.
      if (isExpandedNode && currentTrade?.legsShowing) {
        return "Close Leg Table";
      } else if (isExpandedNode && currentTrade?.allocationsShowing) {
        return "Close Alloc Table";
      } else if (mainBlueAction.id == "Send to Panel" && !isExpandedNode) {
        return "View Trade";
      }
    }
    // For all other buttons, show the normal allocated name - Trades can still action workflow when looking at a leg or allocation table.
    return mainBlueAction.id;
  }

  return (
    <div
      data-testid="parent-container"
      className="flex h-full items-center justify-center"
    >
      <div>
        {mainBlueAction && (
          <Button
            data-testid="MainButton"
            variant="mainBlueAction"
            className={`rounded ${buttonDisabled ? "scale-95 opacity-60" : ""}`}
            size={dynamicButtonSize}
            id={
              mainBlueAction.id.toString().replace(/\s+/g, "") +
              "ActionButtonER"
            }
            onClick={() => {
              if (params.node.data.status == ExecutionStrategyStatus.New) {
                // Read CURRENT state at click time — not the stale closure value
                const freshExpandedRows = cryptoID
                  ? expandedRowsStore.getState().getExpandedRows(cryptoID)
                  : [];
                const currentTrade = freshExpandedRows.find(
                  (obj) => obj.esid == params.data.esid,
                );
                if (currentTrade && isExpandedNode && cryptoID) {
                  // If legs or allocations are open, close the table and send back to the settings:
                  if (
                    currentTrade.legsShowing ||
                    currentTrade.allocationsShowing
                  ) {
                    expandedRowsStore
                      .getState()
                      .updateExpandedRow(cryptoID, params.data.esid, {
                        editPanelShowing: false,
                        allocationsShowing: false,
                        legsShowing: false,
                      });
                    // Set mostRecentlyUpdated so the redraw effect targets THIS row's detail node
                    expandedRowsStore
                      .getState()
                      .setMostRecentlyUpdated(cryptoID, params.data.esid);
                  } else {
                    addDisabledButtonToStoreArray(params.data.esid);
                    newButtonAction(); // sets button disabled and runs logic for view and send to panel.
                  }
                } else {
                  addDisabledButtonToStoreArray(params.data.esid);
                  newButtonAction(); // sets button disabled and runs logic for view and send to panel.
                }
              } else if (verifyTimeInput() == false) {
                generateToastMessageForTradeFlowErrors({
                  action: "other",
                  outcome: "incorrectTimeFormat",
                });
              } else {
                addDisabledButtonToStoreArray(params.data.esid);
                mainBlueAction.function({
                  data: params.node.data,
                  getAccessTokenSilently: getAccessTokenSilently,
                  setButtonDisabled: setButtonDisabled,
                });
              }
            }}
            disabled={buttonDisabled}
          >
            {/* DISPLAY NAME */}
            {determineButtonDisplayName()}
          </Button>
        )}
      </div>
    </div>
  );
};

export default MainActionButtonActionSummary;
